/**
 * @file Компактное отображение доступного трафика и состояния обновления.
 *
 * Для расчётов используется настоящая квота; сглаженное число нужно только
 * экрану. Обновление обозначается вращением кнопки без добавления строки в раскладку.
 */
import { useId } from 'react'

import { useLocale } from '../../i18n/useLocale'
import { useSmoothNumber } from '../transfers/useSmoothNumber'
import './TrafficBalance.css'
import { trafficMessages } from './messages'
import { availableTraffic, formatTrafficBytes, trafficLevel } from './quota'
import type { useTrafficQuota } from './useTrafficQuota'

export default function TrafficBalance({
    quota,
    connected,
}: {
    quota: ReturnType<typeof useTrafficQuota>
    connected: boolean
}) {
    const { locale } = useLocale()
    const text = trafficMessages[locale]

    const titleId = useId()

    // Реальный остаток нужен для решений и цвета; displayedRemaining — лишь для анимации.
    const remaining = quota.data ? availableTraffic(quota.data) : null
    const displayedRemaining = useSmoothNumber(
        remaining ?? 0,
        `${quota.boxId}:${remaining !== null}`,
        true,
    )
    const amount =
        remaining !== null
            ? formatTrafficBytes(displayedRemaining, locale)
            : null

    // Состояние запроса определяет подпись и вращение кнопки, а не числовую цель.
    const refreshing = connected && quota.pending
    const exhausted = remaining === 0 && connected && !quota.error
    const status = !connected
        ? text.waiting
        : quota.pending
          ? quota.data
              ? text.refreshing
              : text.loading
          : quota.error
            ? quota.data
                ? text.stale
                : text.unavailable
            : exhausted
              ? text.exhausted
              : null

    const level =
        remaining !== null && connected && !quota.error
            ? trafficLevel(remaining)
            : 'unknown'
    const levelLabel =
        level === 'unknown'
            ? undefined
            : exhausted
              ? text.exhausted
              : text[level]

    function format(bytes: number) {
        const result = formatTrafficBytes(bytes, locale)

        return `${result.value} ${result.unit}`
    }

    return (
        <section className='traffic-balance' aria-labelledby={titleId}>
            <div className='traffic-balance-row'>
                <div className='traffic-balance-overview'>
                    <h2 id={titleId}>{text.title}</h2>

                    <span
                        className={`traffic-balance-amount traffic-balance-${level}`}
                        title={levelLabel}
                    >
                        {amount ? `${amount.value} ${amount.unit}` : '—'}
                        {levelLabel && (
                            <span className='traffic-balance-sr-only'>
                                {' '}
                                · {levelLabel}
                            </span>
                        )}
                    </span>
                </div>

                {/* Кнопка отражает pending вращением; соседнее меню содержит накопительные счётчики. */}
                <div className='traffic-balance-actions'>
                    <button
                        type='button'
                        className='traffic-balance-refresh'
                        data-refreshing={refreshing || undefined}
                        aria-busy={refreshing}
                        onClick={quota.refresh}
                        disabled={!connected || quota.pending}
                        aria-label={refreshing ? text.refreshing : text.refresh}
                        title={refreshing ? text.refreshing : text.refresh}
                    >
                        <svg
                            width='18'
                            height='18'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='1.7'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            aria-hidden='true'
                        >
                            <path d='M20 7v5h-5M4 17v-5h5M6 7a7 7 0 0 1 12-1l2 3M4 15l2 3a7 7 0 0 0 12-1' />
                        </svg>
                    </button>

                    <details className='traffic-balance-details'>
                        <summary aria-label={text.details} title={text.details}>
                            <svg
                                width='18'
                                height='18'
                                viewBox='0 0 24 24'
                                fill='currentColor'
                                aria-hidden='true'
                            >
                                <circle cx='5' cy='12' r='1.7' />
                                <circle cx='12' cy='12' r='1.7' />
                                <circle cx='19' cy='12' r='1.7' />
                            </svg>
                        </summary>

                        <div className='traffic-balance-panel'>
                            <h3>{text.details}</h3>

                            {quota.data && (
                                <dl>
                                    <div>
                                        <dt>{text.allocated}</dt>
                                        <dd>{format(quota.data.limit)}</dd>
                                    </div>

                                    <div>
                                        <dt>{text.used}</dt>
                                        <dd>{format(quota.data.used)}</dd>
                                    </div>
                                </dl>
                            )}
                            {quota.updatedAt && (
                                <p>
                                    {text.updated}{' '}
                                    <time
                                        dateTime={quota.updatedAt.toISOString()}
                                    >
                                        {new Intl.DateTimeFormat(locale, {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }).format(quota.updatedAt)}
                                    </time>
                                </p>
                            )}
                        </div>
                    </details>
                </div>
            </div>

            {/* Во время обновления подпись доступна скринридеру, но не меняет высоту карточки. */}
            {status && (
                <p
                    className={`traffic-balance-status${refreshing ? ' traffic-balance-sr-only' : ''}`}
                    role='status'
                >
                    {status}
                </p>
            )}
        </section>
    )
}
